export type Role = "CEO" | "CPO" | "CTO" | "member";
export type Actor = {
  id: string;
  name: string;
  role: Role;
  tenantId: string;
  unionId?: string;
  openId?: string;
  userId?: string;
  token?: string;
  isAI?: boolean;
  customerId?: string;
};
export type TaskStatus =
  | "triage"
  | "todo"
  | "in_progress"
  | "in_review"
  | "done"
  | "canceled";
export type Priority = 0 | 1 | 2 | 3 | 4;
export const BUSINESS_DOMAINS = [
  "经营与市场",
  "FDE 交付",
  "课程与培训",
  "产品技术与制度",
  "研究内容与活动",
  "运营中台",
] as const;
export type Task = {
  id: string;
  identifier: string;
  feishuGuid?: string;
  title: string;
  description: string;
  assigneeId: string | null;
  assigneeType?: "person" | "agent";
  completionEvidence?: {
    type: "commit" | "document" | "link";
    reference: string;
  }[];
  lastChangeSource?: string;
  issueFlags?: (
    | string
    | { kind?: string; code?: string; message?: string; reason?: string }
  )[];
  version?: number;
  completedAt?: number | null;
  completedAssigneeType?: "person" | "agent" | null;
  milestoneId?: string | null;
  followerIds: string[];
  dueDate: string | null;
  priority: Priority;
  status: TaskStatus;
  domain: string;
  projectId: string | null;
  sourceUrl: string;
  updatedAt: number;
  createdAt: number;
  prioritySortOrder?: number;
  delegate?: string | null;
  customerId?: string | null;
  blockedBy?: string[];
  blocks?: string[];
  okrId?: string | null;
};
export type Milestone = {
  id: string;
  title: string;
  date: string;
  completed: boolean;
  eventId?: string;
  kind?: "milestone" | "delivery";
  customerId?: string;
};
export type Project = {
  id: string;
  title: string;
  ownerId: string;
  memberIds: string[];
  domain: string;
  status: "planned" | "active" | "paused" | "completed" | "canceled";
  health: "on_track" | "at_risk" | "off_track";
  startDate: string | null;
  targetDate: string | null;
  milestones: Milestone[];
  customerId: string | null;
  keyResultId: string | null;
  tasklistGuid: string;
  progress: number;
  healthEvidence?: string[];
  sourceUrl?: string;
  updates?: unknown[];
};
export type CalendarItem = {
  id: string;
  kind: "task" | "project" | "milestone" | "delivery" | "approval" | "okr";
  ref?: { source: 'tasks' | 'calendar'; id: string; kind: string; version?: number | string };
  capabilities?: { reschedule?: { allowed: boolean; reason?: string; operation?: string; expectedVersion?: number | string } };
  title: string;
  date: string;
  priority: Priority;
  personId?: string | null;
  sourceId: string;
  projectId?: string;
  milestoneId?: string;
  assigneeType?: "person" | "agent";
  issueFlags?: Task["issueFlags"];
};
export type InboxItem = {
  id: string;
  source: "agent" | "mention" | "forward" | "minutes" | "crm" | "inspiration" | "quick";
  title: string;
  content: string;
  sourceUrl: string;
  status: "pending" | "processing" | "triaged" | "returned";
  taskIds: string[];
  createdAt: number;
  customerId?: string | null;
  reason?: string;
};
export type PostTier = "look" | "required" | "co_create";
export type Post = {
  id: string;
  authorId: string;
  authorName?: string;
  title: string;
  url: string;
  quote: string;
  opinion: string;
  tier: PostTier;
  proposal: string;
  tags: string[];
  summary: string[];
  related: unknown[];
  readerIds: string[];
  readBy: string[];
  plusOnes: string[];
  createdAt: number;
  opinions?: unknown[];
  comments?: unknown[];
  approvalId?: string;
  projectId?: string;
  draftUrl?: string;
};
export type Approval = {
  id: string;
  type: "asset" | "delivery" | "publication" | "quote" | "project";
  title: string;
  approvalCode: string;
  instanceCode: string;
  initiatorId: string;
  approverIds: string[];
  ccIds: string[];
  status: string;
  form: Record<string, unknown>;
  dueDate?: string;
  comments?: unknown[];
  tasks?: unknown[];
};
export type OkrKeyResult = {
  id: string;
  title: string;
  progress: number;
  confidence: string;
  ownerId: string;
  projectIds: string[];
  projectProgress?: number;
  objectiveId: string;
};
export type OkrObjective = {
  id: string;
  title: string;
  ownerId: string;
  period: string;
  progress: number;
  confidence: string;
  alignmentIds: string[];
  keyResults: OkrKeyResult[];
};
export type Decision = {
  id: string;
  title: string;
  facts: string;
  assumptions: string;
  alternatives: string;
  costsAndRisks: string;
  conclusion: string;
  deciderId: string;
  domain: string;
  status: "draft" | "confirmed" | "reviewed";
  reviewDate: string | null;
  sourceUrl: string;
  customerId?: string | null;
  createdAt: number;
};
export type Notification = {
  id: string;
  personId: string;
  kind: string;
  title: string;
  content: string;
  sourceId: string;
  sourceUrl: string;
  urgent: boolean;
  readAt: number | null;
  snoozedUntil: number | null;
  createdAt: number;
};
/** Technical disposition preserves the original AI status and unknown send outcome. */
export type PreReviewDisposition = {
  kind: "terminal_not_applicable";
  externalOutcome: "unknown";
  officialStatus: string;
  actorId: string;
  createdAt: number;
  mutationKey: string;
  actionId?: string;
};
export type AiAction = {
  preReviewDisposition?: PreReviewDisposition | null;
  id: string;
  type: string;
  entityType: string;
  entityId: string;
  reason: string;
  before: unknown;
  after: unknown;
  createdAt: number;
  undoneAt: number | null;
  actorId?: string;
};

export type Person = Actor & { active?: boolean };
export type TaskComment = {
  id: string;
  content: string;
  authorId?: string;
  createdAt?: number;
  mentionIds?: string[];
};
export type TaskActivity = {
  id: string;
  kind?: string;
  type?: string;
  content?: string;
  description?: string;
  createdAt?: number;
  before?: unknown;
  after?: unknown;
};
export type SavedView = {
  id: string;
  name: string;
  filters: Record<string, string>;
  layout?: string;
};
export type ProjectUpdate = {
  evidence?: string[];
  generatedBy?: string;
  id: string;
  content: string;
  health?: Project["health"];
  createdAt?: number;
  authorId?: string;
};
export type PlatformSettings = {
  notifications?: {
    focusTime?: string;
    summaryTime?: string;
    weeklyTime?: string;
    dndStart?: string;
    dndEnd?: string;
    weekendDnd?: boolean;
    enabled?: boolean;
  };
  ai?: {
    enabled?: boolean;
    mode?: "automatic" | "suggestion";
    actions?: Record<string, boolean>;
  };
  [key: string]: unknown;
};
